import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import CompetitionCheckboxes from '@/components/CompetitionCheckboxes';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import SoccerViewSwitcher from '@/components/SoccerViewSwitcher';
import UpcomingGameCard from '@/components/UpcomingGameCard';
import BreadcrumbNav from '@/components/seo/BreadcrumbNav';
import PageMeta from '@/components/seo/PageMeta';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { fetchUpcomingGamesData } from '@/api/gamesService';
import { Game, SoccerCompetition, SportType } from '@/types';
import {
  formatSelectedCompetitions,
  getCompetitionUrlSlug,
  getSportDisplayName,
  mapUrlParamToCompetition,
  sortSoccerCompetitions,
} from '@/utils/sportUtils';

type WindowValue = '48h' | '7d' | '14d';

const UPCOMING_WINDOW_STORAGE_KEY = 'skore.upcomingWindowPreference';

const WINDOW_OPTIONS: Array<{ value: WindowValue; label: string; days: number }> = [
  { value: '48h', label: 'Next 48h', days: 2 },
  { value: '7d', label: 'Next 7 days', days: 7 },
  { value: '14d', label: 'Next 2 weeks', days: 14 },
];

const DEFAULT_WINDOW: WindowValue = '48h';

function parseWindowValue(rawValue: string | null): WindowValue | undefined {
  if (rawValue === '48h' || rawValue === '7d' || rawValue === '14d') {
    return rawValue;
  }

  return undefined;
}

function getStoredWindowPreference(): WindowValue | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return parseWindowValue(window.localStorage.getItem(UPCOMING_WINDOW_STORAGE_KEY));
}

function saveWindowPreference(windowValue: WindowValue): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(UPCOMING_WINDOW_STORAGE_KEY, windowValue);
}

function parseCompetitionParams(searchParams: URLSearchParams): SoccerCompetition[] {
  const competitionParam = searchParams.get('competitions');
  if (!competitionParam) {
    return [];
  }

  return Array.from(new Set(
    competitionParam
      .split(',')
      .map(mapUrlParamToCompetition)
      .filter((competition): competition is SoccerCompetition => Boolean(competition) && competition !== 'All')
  ));
}

function buildSearchParams(
  sport: SportType,
  windowValue: WindowValue,
  competitions: SoccerCompetition[]
): URLSearchParams {
  const nextParams = new URLSearchParams();

  if (windowValue !== DEFAULT_WINDOW) {
    nextParams.set('window', windowValue);
  }

  if (sport !== 'soccer') {
    return nextParams;
  }

  const normalizedCompetitions = competitions
    .filter((competition) => Boolean(competition) && competition !== 'All')
    .map((competition) => getCompetitionUrlSlug(competition))
    .filter(Boolean);

  if (normalizedCompetitions.length > 0) {
    nextParams.set('competitions', normalizedCompetitions.join(','));
  }

  return nextParams;
}

function getLocalDayKey(utcDatetime: string): string {
  const date = new Date(utcDatetime);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function getDayLabel(utcDatetime: string): string {
  const date = new Date(utcDatetime);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const isSameDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();

  if (isSameDay(date, today)) {
    return 'Today';
  }

  if (isSameDay(date, tomorrow)) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function getSportWindowDescription(sport: SportType): string {
  if (sport === 'soccer') {
    return 'Next kickoffs by time and competition.';
  }

  return `Next scheduled ${getSportDisplayName(sport)} games.`;
}

interface UpcomingSportPageProps {
  sport: SportType;
}

const UpcomingSportPage = ({ sport }: UpcomingSportPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryWindow = parseWindowValue(searchParams.get('window'));
  const [selectedWindow, setSelectedWindow] = useState<WindowValue>(
    () => queryWindow ?? getStoredWindowPreference() ?? DEFAULT_WINDOW
  );
  const selectedCompetitions = sport === 'soccer' ? parseCompetitionParams(searchParams) : [];
  const selectedWindowConfig = WINDOW_OPTIONS.find((option) => option.value === selectedWindow) ?? WINDOW_OPTIONS[0];
  const sportDisplayName = getSportDisplayName(sport);

  useEffect(() => {
    if (queryWindow) {
      setSelectedWindow(queryWindow);
      saveWindowPreference(queryWindow);
      return;
    }

    const storedWindow = getStoredWindowPreference();
    const nextWindow = storedWindow ?? DEFAULT_WINDOW;
    setSelectedWindow(nextWindow);
    saveWindowPreference(nextWindow);
  }, [queryWindow]);

  const { data: upcomingGamesData, isLoading, error } = useQuery({
    queryKey: ['upcoming-games'],
    queryFn: fetchUpcomingGamesData,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const sourceGames = useMemo(
    () => Object.values(upcomingGamesData?.games?.[sport] ?? {}) as Game[],
    [sport, upcomingGamesData?.games]
  );

  const availableCompetitions = useMemo(
    () => sport === 'soccer'
      ? sortSoccerCompetitions(
          Array.from(new Set(
            sourceGames
              .map((game) => game.competition)
              .filter((competition): competition is SoccerCompetition => Boolean(competition))
          ))
        )
      : [],
    [sourceGames, sport]
  );

  const selectedCompetitionSet = new Set(selectedCompetitions);

  const visibleGames = useMemo(() => {
    const windowStart = new Date();
    const windowEnd = new Date();
    windowEnd.setDate(windowEnd.getDate() + selectedWindowConfig.days);
    windowEnd.setHours(23, 59, 59, 999);

    return sourceGames
      .filter((game) => {
        if (game.status?.type !== 'scheduled' || !game.utcDatetime) {
          return false;
        }

        const kickoff = new Date(game.utcDatetime);
        if (kickoff < windowStart || kickoff > windowEnd) {
          return false;
        }

        if (sport !== 'soccer' || selectedCompetitionSet.size === 0) {
          return true;
        }

        return Boolean(game.competition && selectedCompetitionSet.has(game.competition));
      })
      .sort((left, right) => new Date(left.utcDatetime).getTime() - new Date(right.utcDatetime).getTime());
  }, [selectedCompetitionSet, selectedWindowConfig.days, sourceGames, sport]);

  const groupedGames = useMemo(() => {
    const groups = new Map<string, Game[]>();

    visibleGames.forEach((game) => {
      const key = getLocalDayKey(game.utcDatetime);
      const existing = groups.get(key) ?? [];
      groups.set(key, [...existing, game]);
    });

    return Array.from(groups.entries()).map(([key, games]) => ({
      key,
      label: getDayLabel(games[0].utcDatetime),
      games,
    }));
  }, [visibleGames]);

  const activeCompetitionLabel = selectedCompetitions.length > 0
    ? formatSelectedCompetitions(selectedCompetitions)
    : 'All competitions';

  const heroTitle = `Upcoming ${sportDisplayName} Games`;
  const upcomingDescription = sport === 'soccer'
    ? `Browse the next ${selectedWindowConfig.label.toLowerCase()} of upcoming soccer games on skore.info without mixing schedules into the completed-game flow.`
    : `Browse the next ${selectedWindowConfig.label.toLowerCase()} of ${sportDisplayName.toLowerCase()} on skore.info without mixing them into the completed-game flow.`;
  const upcomingKeywords = sport === 'soccer'
    ? ['upcoming soccer games', 'soccer schedule', 'soccer fixtures', 'next soccer games']
    : [`upcoming ${sport} games`, `${sport} schedule`, `skore ${sport} upcoming`];
  const robotsDirective = sport === 'soccer' ? 'index, follow' : 'noindex, follow';

  const firstKickoffLabel = visibleGames[0]
    ? new Date(visibleGames[0].utcDatetime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'No games in range';

  const handleWindowChange = (windowValue: WindowValue) => {
    setSelectedWindow(windowValue);
    saveWindowPreference(windowValue);
    setSearchParams(buildSearchParams(sport, windowValue, selectedCompetitions), { replace: true });
  };

  const handleCompetitionChange = (competitions: SoccerCompetition[]) => {
    setSearchParams(buildSearchParams(sport, selectedWindow, competitions), { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PageMeta
        title={`${heroTitle} | skore.info`}
        description={upcomingDescription}
        canonicalPath={`/sport/${sport}/upcoming`}
        robots={robotsDirective}
        keywords={upcomingKeywords}
      />

      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <BreadcrumbNav sport={sport} />

        <div className="max-w-4xl">
          <h1 className="text-3xl font-bold text-white md:text-4xl">{heroTitle}</h1>
          <p className="mt-3 text-base leading-7 text-slate-300">
            {getSportWindowDescription(sport)}
          </p>
        </div>

        <SoccerViewSwitcher
          sport={sport}
          mode="upcoming"
          competitions={selectedCompetitions}
          className="mt-6"
        />

        <section className="mt-8 overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(165deg,rgba(15,23,42,0.95),rgba(17,24,39,0.92))] shadow-[0_24px_72px_rgba(2,6,23,0.28)]">
          <div className="grid gap-8 px-6 py-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:px-8 lg:py-8">
            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-300">Local time</p>

              <div className="flex flex-wrap gap-2">
                {WINDOW_OPTIONS.map((option) => {
                  const isActive = option.value === selectedWindow;

                  return (
                    <Button
                      key={option.value}
                      onClick={() => handleWindowChange(option.value)}
                      className={isActive
                        ? 'rounded-full border border-indigo-400/40 bg-indigo-500 text-white hover:bg-indigo-500'
                        : 'rounded-full border border-white/10 bg-slate-950/70 text-slate-300 hover:bg-slate-800 hover:text-white'}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <Card className="border-white/10 bg-slate-950/65 text-white">
              <CardContent className="px-5 py-5">
                <dl className={`grid gap-4 ${sport === 'soccer' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                  <div>
                    <dt className="text-sm text-slate-400">Window</dt>
                    <dd className="mt-1 text-lg font-semibold text-white">{selectedWindowConfig.label}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-400">Starts</dt>
                    <dd className="mt-1 text-lg font-semibold text-white">{firstKickoffLabel}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-400">Games</dt>
                    <dd className="mt-1 text-lg font-semibold text-white">{visibleGames.length}</dd>
                  </div>
                  {sport === 'soccer' && (
                    <div>
                      <dt className="text-sm text-slate-400">Competition</dt>
                      <dd className="mt-1 text-lg font-semibold text-white">{activeCompetitionLabel}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>
        </section>

        {sport === 'soccer' && (
          <div className="mt-8">
            <CompetitionCheckboxes
              availableCompetitions={availableCompetitions}
              selectedCompetitions={selectedCompetitions}
              onChange={handleCompetitionChange}
            />
          </div>
        )}

        {isLoading ? (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="h-[320px] animate-pulse rounded-3xl border border-white/10 bg-slate-900/60" />
            ))}
          </div>
        ) : error ? (
          <div className="mt-10 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-4 text-red-100">
            Couldn&apos;t load upcoming games.
          </div>
        ) : visibleGames.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-white/10 bg-slate-900/72 px-6 py-10 text-center">
            <h2 className="text-xl font-semibold text-white">No games in this window.</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Try a wider range{sport === 'soccer' ? ' or clear filters' : ''}.
            </p>
          </div>
        ) : (
          <div className="mt-10 space-y-10">
            {groupedGames.map((group) => (
              <section key={group.key}>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-2xl font-semibold text-white">{group.label}</h2>
                    <p className="text-sm text-slate-400">{group.games.length} game{group.games.length === 1 ? '' : 's'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {group.games.map((game) => (
                    <UpcomingGameCard key={game.id} game={game} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default UpcomingSportPage;
